import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { fmtCount, fullDate } from '../lib/format';
import { api, ApiError } from '../api';
import { Aliases } from '../components/Aliases';
import { DnsFlags } from '../components/DnsRecords';
import { useDialog } from '../components/Dialog';
import { step } from '../telemetry';
import type {
  DeliveryDomain,
  DeliveryReport,
  DmarcSummary,
  DnsRecord,
  Domain,
  Mailbox,
  User,
} from '../types';

interface Props {
  user: User;
}

interface Health {
  status: 'ok' | 'warn' | 'error' | 'stale' | 'unknown';
  ranAt: string | null;
  stale: boolean;
  problems?: number;
  checks: { id: string; severity: string; summary: string }[];
  note?: string;
}

const CHECK_LABEL: Record<string, string> = {
  disk: 'Disk sahəsi',
  queue: 'Poçt növbəsi',
  cert: 'TLS sertifikatı',
  backup: 'Yedəkləmə',
  dkim: 'DKIM imzalama',
  database: 'Baza',
};

interface Usage {
  hour: number;
  day: number;
  hourlyLimit: number;
  dailyLimit: number;
  pool: string;
}

/**
 * Limitin nə qədərinin işlədildiyini göstərir.
 *
 * Rəng 80%-dən sonra dəyişir — məqsəd limit dolmadan ƏVVƏL xəbərdar etməkdir.
 * Doldu-dolmadı yalnız məktub getmədikdə bilinsə, xəbərdarlığın faydası olmaz.
 */
function Meter({ label, used, limit }: { label: string; used: number; limit: number }): React.ReactElement {
  const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const tone = pct >= 100 ? 'is-full' : pct >= 80 ? 'is-high' : '';
  return (
    <div className="usage">
      <div className="usage-head">
        <span>{label}</span>
        <b>
          {used} / {limit}
        </b>
      </div>
      <div className="usage-bar">
        <div className={`usage-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function messageFor(code: string): string {
  switch (code) {
    case 'weak_password':
      return 'Şifrə ən azı 10 simvol olmalıdır.';
    case 'last_admin':
      return 'Bu, son admin qutusudur — deaktiv edilə və ya silinə bilməz.';
    case 'exists':
      return 'Bu ünvan artıq mövcuddur.';
    /*
      ⚠️ MƏTN «ARTIQ MÖVCUDDUR»DAN FƏRQLİDİR — QƏSDƏN.
      Burada ünvan qutu kimi yox, BAŞQA şirkətin panel hesabı kimi
      qeydiyyatdadır. «Artıq mövcuddur» desək, müştəri öz qutu
      siyahısında axtarar, tapmaz və nə edəcəyini bilməz. Səbəb açıq
      deyilir ki, dəstəyə müraciət edə bilsin.
    */
    case 'used_by_account':
      return 'Bu ünvanla artıq panel hesabı qeydiyyatdadır və o, başqa şirkətə aiddir. Qutu yaradıla bilməz — dəstəyə yazın.';
    case 'invalid_local_part':
      return 'Ünvanın adı yalnız hərf, rəqəm, nöqtə və defis ola bilər.';
    case 'domain_not_active':
      return 'Domen hələ aktiv deyil — əvvəlcə DNS yoxlamasından keçirin.';
    case 'quota_exceeded':
      return 'Paketinizin qutu limiti dolub.';
    case 'not_found':
      return 'Tapılmadı — səhifəni yeniləyin.';
    case 'dns_pending':
      return 'DNS hələ hazır deyil. Yazıları əlavə etdikdən sonra bir neçə dəqiqə gözləyib yenidən yoxlayın.';
    case 'invalid_domain':
      return 'Domen adı düzgün deyil. Nümunə: sirketiniz.az';
    case 'already_exists':
      return 'Bu domen artıq əlavə edilib.';
    case 'limit_reached':
      return 'Domen limiti dolub (25).';
    default:
      return 'Əməliyyat alınmadı. Bir azdan yenidən yoxlayın.';
  }
}

// ── ÇATDIRILMA NƏZARƏTİ — YALNIZ PLATFORMA OPERATORU ────────────────────

/*
  ⚠️ NİYƏ AYRI BÖLMƏ — «Sistemin vəziyyəti» KİFAYƏT ETMİR.
  Sağlamlıq yoxlaması bütün platforma üçün BİR sətir verir: «10 domenin
  DKIM açarı diskdə yoxdur». Bu, problemin VARLIĞINI bildirir, amma
  düzəltmək üçün HANSI domen olduğunu bilmək lazımdır. Bu cədvəl həmin
  ünvanı verir.

  ⚠️ HƏR SÜTUN AYRI BİR SƏSSİZ NASAZLIĞIN QARŞILIĞIDIR (2026-08-18, bir gün):
    • DNS bayraqları — domen bazada «MX qaydasındadır» yazılıb, real DNS-də
      MX 13 gündür yoxdur. Səbəb: DNS yalnız müştəri «Yoxla» basanda
      yoxlanılırdı, sonra bir daha heç vaxt.
    • DKIM açarı — DNS-də publik açar dərc olunub, diskdə gizli açar yoxdur.
      Domen İMZASIZ göndərir, DNS bayrağı isə yaşıl qalır: 12 domendən 10-u
      aylarla belə işlədi.
    • Son yoxlama — tarix donubsa, nəzarətin ÖZÜ sınıb. Bu, cədvəldəki ən
      aldadıcı vəziyyətdir: bütün bayraqlar yaşıl görünür, çünki heç nə
      yoxlanmır.
    • DMARC keçmə — bizim jurnalımız yalnız «SMTP-yə verdik» deyir. Məktubun
      qarşı tərəfdə necə qiymətləndirildiyini yalnız bu sütun göstərir.
*/

/** Bundan yuxarı keçmə faizi normaldır. */
const RATE_GOOD = 95;
/** Bundan aşağısı XƏTA sayılır — domenin poçtu real problemdədir. */
const RATE_BAD = 80;

/**
 * DNS yoxlaması bundan köhnədirsə, dövri nəzarətin özü dayanıb.
 *
 * Dövrə gündə bir dəfə işləyir; iki gün marja qəsdən qoyulub ki, bir dəfə
 * buraxılmış dövrə hər sətri sarıya boyayıb siqnalı dəyərsizləşdirməsin.
 */
const STALE_HOURS = 48;

/** Keçmə faizinə görə mövcud `.state` tonları. */
function rateTone(pct: number): string {
  return pct >= RATE_GOOD ? 'is-ok' : pct >= RATE_BAD ? 'is-warn' : 'is-bad';
}

/**
 * `DnsFlags` tam `Domain` tipi gözləyir, halbuki yalnız dörd bayrağa baxır.
 *
 * ⚠️ BAYRAQLARI BURADA TƏKRAR ÇƏKMƏK OLMAZ. İki nüsxə vaxtla ayrılır və
 * eyni domen iki ekranda iki fərqli vəziyyət göstərir — DNS-də bu, birbaşa
 * yanlış qərar deməkdir (bax: components/DnsRecords.tsx başlığı). Ona görə
 * çatdırılma sətrindən komponentin gözlədiyi minimal obyekt qurulur;
 * qalan sahələr bu ekranda ümumiyyətlə oxunmur.
 */
function flagsOf(d: DeliveryDomain): Domain {
  return {
    id: d.domain,
    domain: d.domain,
    verified: d.active,
    active: d.active,
    isPrimary: false,
    mxOk: d.mxOk,
    spfOk: d.spfOk,
    dkimOk: d.dkimOk,
    dmarcOk: d.dmarcOk,
    checkedAt: d.checkedAt,
    mailboxCount: 0,
  };
}

/** Son DNS yoxlaması — köhnəlik ayrıca vurğulanır. */
function CheckedCell({ at }: { at: string | null }): React.ReactElement {
  if (!at) {
    return (
      <span
        className="state is-bad"
        title="Bu domen aktivləşəndən sonra bir dəfə də yoxlanmayıb"
      >
        heç vaxt
      </span>
    );
  }
  const ms = new Date(at).getTime();
  const hours = Number.isNaN(ms) ? 0 : (Date.now() - ms) / 3_600_000;
  if (hours > STALE_HOURS) {
    return (
      <span
        className="state is-warn"
        title="Dövri yoxlama işləmir — yanındakı bayraqlar KÖHNƏ məlumatdır"
      >
        {fullDate(at)}
      </span>
    );
  }
  return <span className="dim">{fullDate(at)}</span>;
}

/**
 * Hesabatların sonuncu dəfə nə vaxt yığıldığı.
 *
 * ⚠️ TOPLAMA DAYANSA, CƏDVƏLDƏKİ FAİZLƏR DONUR — və donmuş 100% canlı
 * 100%-dən fərqlənmir. Yəni nəzarətin özünün sınması yenə səssiz olardı.
 * Köhnə tarixin vurğulanması bunun yeganə əlamətidir.
 */
function CollectStamp({ at }: { at: string | null }): React.ReactElement {
  if (!at) {
    return <span className="state is-warn">hesabat heç vaxt yığılmayıb</span>;
  }
  const ms = new Date(at).getTime();
  const hours = Number.isNaN(ms) ? 0 : (Date.now() - ms) / 3_600_000;
  if (hours > STALE_HOURS) {
    return (
      <span
        className="state is-warn"
        title="Toplama dayanıb — aşağıdakı faizlər köhnə məlumatdır"
      >
        Son toplama: {fullDate(at)}
      </span>
    );
  }
  return <span className="dlv-sub">Son toplama: {fullDate(at)}</span>;
}

/**
 * DMARC keçmə faizi.
 *
 * ⚠️ HESABAT YOXDURSA SIFIR YAZILMIR. «0%» oxuyan operator domenin
 * sınıq olduğunu düşünərdi, halbuki heç bir məlumatımız yoxdur — bu iki
 * hal tam fərqlidir və qarışdırılması yalançı təşviş deməkdir.
 */
function RateCell({ s }: { s: DmarcSummary | null }): React.ReactElement {
  if (!s || s.total <= 0) {
    return <span className="dim" title="Bu domen üçün hələ hesabat gəlməyib">—</span>;
  }
  const pct = Math.round((s.pass / s.total) * 100);
  return (
    <>
      <span className={`state ${rateTone(pct)}`}>{pct}%</span>
      <span className="dlv-sub">
        {fmtCount(s.total)} məktub · DKIM sınıq {fmtCount(s.dkimFail)} · SPF sınıq{' '}
        {fmtCount(s.spfFail)}
      </span>
    </>
  );
}

/** Bütün domenlər üzrə birləşdirilmiş göndərən IP. */
interface SourceRow {
  ip: string;
  count: number;
  pass: number;
  domains: string[];
}

/**
 * IP-ləri domenlərin üstündən birləşdirir.
 *
 * ⚠️ NƏTİCƏ TAM SİYAHI DEYİL. Arxa tərəf domen başına yalnız ən çox
 * göndərən 10 IP-ni qaytarır, ona görə buradan «başqa IP yoxdur» nəticəsi
 * ÇIXARMAQ OLMAZ. Az göndərən saxta mənbə siyahının quyruğunda qala bilər.
 */
function sourceRows(rep: DeliveryReport): SourceRow[] {
  const map = new Map<string, SourceRow>();
  for (const d of rep.domains) {
    if (!d.dmarc) continue;
    for (const s of d.dmarc.sources) {
      const row = map.get(s.ip) ?? { ip: s.ip, count: 0, pass: 0, domains: [] };
      row.count += s.count;
      row.pass += s.pass;
      if (!row.domains.includes(d.domain)) row.domains.push(d.domain);
      map.set(s.ip, row);
    }
  }
  return [...map.values()].sort((a, b) => b.count - a.count);
}

function Delivery(): React.ReactElement {
  const [rep, setRep] = useState<DeliveryReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Düymələrin nəticəsi. `bad` — zolaq xəbərdarlıq tonunda göstərilir. */
  const [note, setNote] = useState<{ text: string; bad: boolean } | null>(null);
  const [busy, setBusy] = useState<'dns' | 'dmarc' | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const r = await api.delivery();
      setRep(r);
      setError(null);
    } catch (err) {
      /*
        ⚠️ XƏTA UDULMUR — bölmənin bütün mənası nasazlığı görünən etməkdir.
        Sorğu sınanda sadəcə boş cədvəl göstərsək, operator «hər şey
        qaydasındadır» kimi oxuyar. Kod açıq yazılır (`http_404` və s.),
        çünki bu ekranı yalnız biz görürük və uc dəyişəndə səbəb dərhal
        görünməlidir.
      */
      setError(
        err instanceof ApiError
          ? `Çatdırılma məlumatı alınmadı: ${err.code}`
          : 'Çatdırılma məlumatı alınmadı.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
    ⚠️ ADMIN-DƏKİ ORTAQ `run()` İŞLƏDİLMİR — QƏSDƏN. O, bütün ekranı
    yenidən yükləyir və yalnız «hazırdır» yazır. Burada isə RƏQƏMLƏR
    nəticənin özüdür: «0 domen yoxlandı» ilə «12 domen yoxlandı, 2 sındı»
    tamamilə fərqli xəbərdir və birincisi «hazırdır» arxasında gizlənərdi.
  */
  async function act(which: 'dns' | 'dmarc'): Promise<void> {
    setBusy(which);
    setNote(null);
    setError(null);
    try {
      if (which === 'dns') {
        const r = await api.recheckDns();
        setNote({
          bad: r.broken > 0 || r.unreachable > 0,
          text:
            `${r.checked} domen yoxlandı · ${r.broken} sındı · ` +
            `${r.recovered} bərpa oldu · ${r.unreachable} soruşula bilmədi`,
        });
      } else {
        const r = await api.collectDmarc();
        setNote({
          bad: r.failed > 0,
          text:
            `${r.scanned} məktub oxundu · ${r.stored} yeni hesabat · ` +
            `${r.skipped} təkrar (artıq bazadadır) · ${r.failed} emal oluna bilmədi`,
        });
      }
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? `Əməliyyat alınmadı: ${err.code}` : 'Əməliyyat alınmadı.',
      );
    } finally {
      setBusy(null);
    }
  }

  const rows = rep ? sourceRows(rep) : [];
  /*
    ⚠️ BOŞ SİYAHI «SAXTAKARLIQ YOXDUR» DEMƏK DEYİL. Hesabat gəlməyibsə
    heç nə bilmirik — ona görə cədvəl əvəzinə izah göstərilir.
  */
  const known = rep !== null && rep.ourIps.length > 0;

  return (
    <section className="admin-card dlv-card">
      <h2>Çatdırılma</h2>
      <p className="onboard-note">
        Bu bölmə poçtun bizdən ÇIXDIĞINI yox, qarşı tərəfə NECƏ çatdığını
        göstərir. Serverin jurnalı yalnız «SMTP-yə verildi» faktını bilir;
        imzanın yoxlanışdan keçib-keçmədiyi və məktubun qutuya, yoxsa spama
        düşdüyü orada ümumiyyətlə yazılmır.
      </p>

      <div className="dlv-actions">
        <button
          type="button"
          className="btn-secondary"
          disabled={busy !== null}
          onClick={() => void act('dns')}
        >
          {busy === 'dns' ? 'Yoxlanılır…' : 'DNS-i indi yoxla'}
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={busy !== null}
          onClick={() => void act('dmarc')}
        >
          {busy === 'dmarc' ? 'Yığılır…' : 'DMARC hesabatlarını yığ'}
        </button>
        {rep ? <CollectStamp at={rep.lastCollectAt} /> : null}
      </div>

      {error ? (
        <p className="login-error" role="alert">
          {error}
        </p>
      ) : null}
      {note ? (
        <p className={note.bad ? 'banner is-warn' : 'banner is-ok'}>{note.text}</p>
      ) : null}

      {loading ? <div className="spinner" aria-label="Yüklənir" /> : null}

      {rep && rep.domains.length === 0 ? (
        <p className="onboard-note">Aktiv domen yoxdur.</p>
      ) : null}

      {rep && rep.domains.length > 0 ? (
        <>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Domen</th>
                  <th>DNS</th>
                  <th>DKIM açarı</th>
                  <th>Son DNS yoxlaması</th>
                  <th>DMARC keçmə ({rep.days} gün)</th>
                </tr>
              </thead>
              <tbody>
                {rep.domains.map((d) => (
                  <tr key={d.domain} className={d.active ? '' : 'is-off'}>
                    <td>
                      <code>{d.domain}</code>
                      {d.active ? null : <span className="tag">aktiv deyil</span>}
                    </td>
                    <td>
                      <DnsFlags d={flagsOf(d)} />
                    </td>
                    <td>
                      {/*
                        ⚠️ BU SÜTUN DNS BAYRAQLARINI TƏKRARLAMIR. «DKIM»
                        bayrağı DNS-dəki PUBLİK açarı, bu sütun isə rspamd-ın
                        oxuduğu GİZLİ açar faylını göstərir. Məhz bu iki
                        şeyin fərqi 10 domeni aylarla imzasız buraxdı.
                      */}
                      {d.dkimKeyOnDisk ? (
                        <span className="state is-ok">var</span>
                      ) : (
                        <span
                          className="state is-bad"
                          title={
                            'Bazada açar var və DNS-də publik açar dərc olunub, amma ' +
                            'rspamd üçün fayl çıxarılmayıb — bu domen İMZASIZ göndərir.'
                          }
                        >
                          diskdə YOXDUR
                        </span>
                      )}
                    </td>
                    <td>
                      <CheckedCell at={d.checkedAt} />
                    </td>
                    <td>
                      <RateCell s={d.dmarc} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Mənbə IP-ləri</h3>
          <p className="onboard-note">
            Domenlərimiz adından KİMİN göndərdiyini yalnız bu cədvəl göstərir —
            başqa mənbəyimiz yoxdur. Öz serverimizin IP-si gözləniləndir. Yad IP
            iki şey ola bilər: <strong>yönləndirmə</strong> (məktub üçüncü
            serverdən keçib — SPF sınır, DKIM saxlanıldığı üçün DMARC adətən
            yenə keçir) və ya <strong>saxtakarlıq</strong> (kimsə domenimiz
            adından göndərir — belə sətirdə keçmə demək olar sıfır olur). Qərar
            IP-nin özünə görə yox, sətrin keçmə faizinə görə verilir.
          </p>

          {rows.length === 0 ? (
            <p className="onboard-note">
              Hələ heç bir DMARC hesabatı yığılmayıb. Hesabatlar qəbuledicilərdən
              (Google, Microsoft, Yahoo) gündə bir dəfə gəlir; yeni domendə ilk
              hesabat 24–48 saat sonra görünür. Ümumiyyətlə gəlməsi üçün domenin
              DMARC qeydində <code>rua</code> ünvanı olmalıdır.
            </p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>IP</th>
                    <th>Mənbə</th>
                    <th>Məktub</th>
                    <th>Keçmə</th>
                    <th>Domen</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const pct = r.count > 0 ? Math.round((r.pass / r.count) * 100) : 0;
                    const ours = rep.ourIps.includes(r.ip);
                    return (
                      <tr key={r.ip}>
                        <td>
                          <code>{r.ip}</code>
                        </td>
                        <td>
                          {/*
                            ⚠️ `ourIps` BOŞDURSA HEÇ NƏ İDDİA EDİLMİR.
                            Serverin IP siyahısını bilmədən «yad» yazmaq
                            yalançı ittihamdır və operatoru həqiqi hadisədən
                            yayındırar.
                          */}
                          {!known ? (
                            <span className="dim">—</span>
                          ) : ours ? (
                            <span className="tag">bizim server</span>
                          ) : (
                            <span className="state is-bad">yad</span>
                          )}
                        </td>
                        <td>{fmtCount(r.count)}</td>
                        <td>
                          <span className={`state ${rateTone(pct)}`}>{pct}%</span>
                        </td>
                        <td className="dim">
                          {r.domains.slice(0, 3).join(', ')}
                          {r.domains.length > 3 ? ` +${r.domains.length - 3}` : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

export function Admin({ user }: Props): React.ReactElement {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [boxes, setBoxes] = useState<Mailbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  // ⚠️ `window.confirm` əvəzi — bax components/Dialog.tsx
  const dlg = useDialog();

  /**
   * Yeni domen forması.
   *
   * ⚠️ ONBOARDING-DƏN SONRA DOMEN ƏLAVƏ ETMƏYİN YOLU YOX İDİ.
   * Sihirbaz yalnız BİR dəfə, ilk domen üçün işləyir; şirkət ikinci
   * domen alanda (yeni brend, .com versiyası, satın alınmış şirkət)
   * onu sistemə salmaq mümkün deyildi. Arxa tərəfdə `POST /api/domains`
   * onsuz da 25 domenə qədər dəstəkləyirdi — yalnız düymə yox idi.
   */
  const [newDomain, setNewDomain] = useState('');
  const [dnsFor, setDnsFor] = useState<{ domain: string; records: DnsRecord[] } | null>(null);

  // Yeni qutu formu
  const [newLocal, setNewLocal] = useState('');
  const [newDomainId, setNewDomainId] = useState('');
  const [newPass, setNewPass] = useState('');

  // Şifrə sıfırlama — hansı qutu üçün açıqdır
  const [resetFor, setResetFor] = useState<string | null>(null);
  const [resetPass, setResetPass] = useState('');

  const load = useCallback(async () => {
    try {
      const [d, m, u] = await Promise.all([
        api.domains(),
        api.mailboxes(),
        // ⚠️ Uğursuzluq bütün ekranı sındırmamalıdır — göndəriş sayğacı
        // əlavə məlumatdır, domenlər və qutular isə əsas funksiyadır.
        api.sendUsage().catch(() => null),
      ]);
      // ⚠️ SAĞLAMLIQ YALNIZ PLATFORMA OPERATORU ÜÇÜN SORUŞULUR.
      // Bu bölmə serverin diski, yedəyin ölçüsü, TLS müddəti və BÜTÜN
      // kirayəçilərdəki qutuların cəmi kimi PLATFORMA məlumatını
      // göstərir — müştəriyə aid deyil. Arxa tərəf onsuz da 404 verir
      // (`requirePlatformAdmin`), amma sorğunu ümumiyyətlə göndərməmək
      // daha düzgündür: hər müştəri panel açanda boş yerə rədd cavabı
      // alıb jurnalı doldurmasın.
      if (user.isPlatformAdmin) {
        // Uğursuzluğu qalan məlumatı bloklamamalıdır — ayrıca yüklənir.
        void api.systemHealth().then(setHealth).catch(() => undefined);
      }
      setDomains(d.domains);
      setBoxes(m.mailboxes);
      if (u) setUsage(u);
      const active = d.domains.find((x) => x.active);
      if (active) setNewDomainId((prev) => prev || active.id);
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(what: string, fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      step(what);
      await fn();
      setNotice(`${what} — hazırdır.`);
      await load();
    } catch (err) {
      setError(messageFor(err instanceof ApiError ? err.code : 'unknown'));
    } finally {
      setBusy(false);
    }
  }

  async function addDomain(e: FormEvent): Promise<void> {
    e.preventDefault();
    const name = newDomain.trim();
    if (!name) return;
    await run(`«${name}» əlavə olunur`, async () => {
      const r = await api.addDomain(name);
      setNewDomain('');
      // ⚠️ DNS YAZILARI DƏRHAL AÇILIR. Domen əlavə edilib, amma nə
      // edəcəyini deməsək, istifadəçi «əlavə olundu» görüb dayanır və
      // domen həmişəlik təsdiqlənməmiş qalır.
      const dns = await api.domainDns(r.id);
      setDnsFor({ domain: dns.domain, records: dns.records });
    });
  }

  async function verifyDomain(id: string, name: string): Promise<void> {
    await run(`«${name}» yoxlanılır`, async () => {
      const r = await api.verifyDomain(id);
      if (!r.activated) throw new ApiError(400, 'dns_pending', {});
    });
  }

  async function createBox(e: FormEvent): Promise<void> {
    e.preventDefault();
    const dom = domains.find((d) => d.id === newDomainId);
    if (!dom) return;
    await run(`Qutu yaradılır: ${newLocal}@${dom.domain}`, async () => {
      await api.createMailbox({
        domainId: newDomainId,
        localPart: newLocal.trim().toLowerCase(),
        password: newPass,
      });
      setNewLocal('');
      setNewPass('');
    });
  }

  if (loading) return <div className="admin"><div className="spinner" aria-label="Yüklənir" /></div>;

  const activeDomains = domains.filter((d) => d.active);

  return (
    <div className="admin">
      {dlg.node}
      <h1>Şirkət idarəsi</h1>

      {error ? <p className="login-error" role="alert">{error}</p> : null}
      {notice && !error ? <p className="banner is-ok">{notice}</p> : null}

      {/* ── Sistemin vəziyyəti — YALNIZ PLATFORMA OPERATORU ──
          ⚠️ Şərtdə `user.isPlatformAdmin` DƏ VAR, təkcə `health` yox.
          `health` özü artıq null qalır (yuxarıdakı sorğu göndərilmir),
          amma iki müdafiə qəsdəndir: gələcəkdə kimsə məlumatı başqa
          yerdən doldursa, bölmə yenə də açılmamalıdır. Burada sızan
          şey serverin ölçüsü və müştəri sayıdır — səhvin qiyməti
          bir sətir şərtdən qat-qat baha başa gəlir.
          «Köhnə» vəziyyəti ayrıca göstərilir: yoxlama dayanıbsa, sonuncu
          nəticəni «hər şey yaxşıdır» kimi göstərmək ən təhlükəli
          variantdır — məhz nəzarətin sınması gizlənərdi. */}
      {user.isPlatformAdmin && health && (
        <section className="admin-card">
          <h2>
            Sistemin vəziyyəti{' '}
            <span className={`hb hb-${health.stale ? 'stale' : health.status}`}>
              {health.stale
                ? 'köhnə məlumat'
                : health.status === 'ok'
                  ? 'qaydasındadır'
                  : health.status === 'warn'
                    ? `${health.problems} xəbərdarlıq`
                    : health.status === 'error'
                      ? `${health.problems} problem`
                      : 'naməlum'}
            </span>
          </h2>
          {health.note && <p className="mig-warn">{health.note}</p>}
          <ul className="hc-list">
            {health.checks.map((c) => (
              <li key={c.id} className={`hc hc-${c.severity}`}>
                <span className="hc-mark" aria-hidden="true">
                  {c.severity === 'ok' ? '✓' : c.severity === 'warn' ? '▲' : '✗'}
                </span>
                <span className="hc-label">{CHECK_LABEL[c.id] ?? c.id}</span>
                <span className="hc-text">{c.summary}</span>
              </li>
            ))}
          </ul>
          {health.ranAt && (
            <p className="onboard-note">
              Son yoxlama: {fullDate(health.ranAt)} · saatda bir təkrarlanır
            </p>
          )}
        </section>
      )}

      {/* ── Çatdırılma — YALNIZ PLATFORMA OPERATORU ──
          ⚠️ Şərt «Sistemin vəziyyəti» ilə eyni səbəbdəndir: cədvəldə
          BÜTÜN kirayəçilərin domenləri var. Sorğu da yalnız burada
          göndərilir — komponent quraşdırılmayanda `api.delivery()`
          ümumiyyətlə çağırılmır. */}
      {user.isPlatformAdmin && <Delivery />}

      {/* ── Göndəriş limiti ──
          Operator öz istifadəsini görməlidir. Görmədən limitin doldu-dolmadığı
          yalnız məktub getmədikdə bilinər — bu isə çox gec olar. */}
      {usage && (
        <section className="admin-card">
          <h2>Göndəriş limiti</h2>
          <div className="usage-row">
            <Meter label="Bu saat" used={usage.hour} limit={usage.hourlyLimit} />
            <Meter label="Son 24 saat" used={usage.day} limit={usage.dailyLimit} />
          </div>
          <p className="onboard-note">
            Sayğac ALICI üzrədir — 10 nəfərə göndərilən bir məktub 10 sayılır.
            Limitə çatanda məktublar itmir, növbədə gözləyir və limit
            yeniləndikdə avtomatik göndərilir.
            {usage.pool === 'quarantine' && (
              <> <strong>Hesabınız müvəqqəti məhdudlaşdırılıb — bizimlə əlaqə saxlayın.</strong></>
            )}
          </p>
        </section>
      )}

      {/* ── Domenlər ── */}
      <section className="admin-card">
        <h2>Domenlər</h2>
        {domains.length === 0 ? (
          <p className="onboard-note">Hələ domen əlavə edilməyib.</p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Domen</th>
                <th>Vəziyyət</th>
                <th>DNS</th>
                <th>Qutu</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {domains.map((d) => (
                <tr key={d.id}>
                  <td>
                    <code>{d.domain}</code>
                    {d.isPrimary ? <span className="tag">əsas</span> : null}
                  </td>
                  <td>
                    {d.active ? (
                      <span className="state is-ok">aktiv</span>
                    ) : (
                      <span className="state is-wait">gözləyir</span>
                    )}
                  </td>
                  <td><DnsFlags d={d} /></td>
                  <td>{d.mailboxCount}</td>
                  <td className="tbl-actions">
                    {/*
                      ⚠️ «YOXLA» YALNIZ TƏSDİQLƏNMƏMİŞ DOMENDƏ.
                      Aktiv domendə düymə göstərmək «nəsə düzgün deyil»
                      təəssüratı yaradır.
                    */}
                    {!d.active && (
                      <button
                        className="btn-text is-primary"
                        disabled={busy}
                        onClick={() => void verifyDomain(d.id, d.domain)}
                      >
                        Yoxla
                      </button>
                    )}
                    <button
                      className="btn-text"
                      disabled={busy}
                      onClick={() => {
                        void api
                          .domainDns(d.id)
                          .then((r) => setDnsFor({ domain: r.domain, records: r.records }))
                          .catch(() => setError('DNS yazıları alınmadı.'));
                      }}
                    >
                      DNS yazıları
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

          {/*
            YENİ DOMEN — cədvəlin altında.
            ⚠️ Şirkət ikinci domen alanda (yeni brend, .com versiyası,
            satın alınmış şirkət) onu sistemə salmağın yolu yox idi.
            Onboarding sihirbazı yalnız BİR dəfə, ilk domen üçün işləyir.
            Arxa tərəf isə 25 domenə qədər onsuz da dəstəkləyirdi.
          */}
          <form className="alias-form" onSubmit={addDomain}>
            <label className="field">
              <span>Yeni domen</span>
              <input
                placeholder="sirketiniz.az"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
              />
              {/*
                ⚠️ QİYMƏT DÜYMƏYƏ BASMAZDAN ƏVVƏL DEYİLİR.
                Tarif domen başınadır: hər yeni domen aylıq hesaba 5 AZN
                əlavə edir. Bunu yalnız fakturada göstərsək, müştəri
                məbləğin iki qat olduğunu bir ay sonra görər və xəta
                sanar. Rəqəm burada, qərar anında olmalıdır.

                ⚠️ SABİT RƏQƏM — QƏSDƏN. Bu formanın `BillingSummary`
                sorğusu yoxdur və yalnız bu sətri göstərmək üçün ekrana
                əlavə şəbəkə sorğusu qoymaq gecikmə deməkdir. Tarif
                dəyişsə, axtarış üçün: `PRICE_PER_DOMAIN_MINOR`.
              */}
              <small className="field-hint">
                Hər domen aylıq hesaba <strong>5 AZN</strong> əlavə edir; həmin
                domendə açacağınız qutuların sayı isə limitsizdir. Əlavə
                etdikdən sonra DNS yazıları göstəriləcək — onları
                registrarınızda qurub «Yoxla» düyməsini basın.
              </small>
            </label>
            <button type="submit" className="btn-primary" disabled={busy || !newDomain.trim()}>
              {busy ? 'Əlavə olunur…' : 'Domen əlavə et'}
            </button>
          </form>

          {/*
            DNS YAZILARI — açılan istinad cədvəli.
            ⚠️ Onboarding-dəki tam kartlar burada TƏKRARLANMIR: orada
            addım-addım sihirbaz kontekstidir, burada isə sürətli istinad
            lazımdır. Eyni komponenti iki məqsədə uyğunlaşdırmaq hər
            ikisini pisləşdirərdi.
          */}
          {dnsFor && (
            <div className="admin-dns">
              <div className="admin-dns-head">
                <h3>
                  <code>{dnsFor.domain}</code> üçün DNS yazıları
                </h3>
                <button className="btn-text" onClick={() => setDnsFor(null)}>
                  Bağla
                </button>
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Növ</th>
                      <th>Ad</th>
                      <th>Dəyər</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dnsFor.records.map((r, i) => (
                      <tr key={i}>
                        <td>
                          <code>{r.type}</code>
                          {r.priority !== undefined && (
                            <span className="dns-alt">prio {r.priority}</span>
                          )}
                        </td>
                        <td>
                          <code className="dns-cell">{r.name}</code>
                          {r.nameAlt && <span className="dns-alt">və ya {r.nameAlt}</span>}
                        </td>
                        <td>
                          <code className="dns-cell">
                            {r.type === 'SRV' && r.target
                              ? `${r.weight} ${r.port} ${r.target}`
                              : r.value}
                          </code>
                          {r.replacesExisting && (
                            <span className="dns-alt">mövcudları əvəz edir</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </section>

      {/* ── Poçt qutuları ── */}
      <section className="admin-card">
        <h2>Poçt qutuları</h2>

        {boxes.length === 0 ? (
          <p className="onboard-note">Hələ qutu yoxdur.</p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Ünvan</th>
                <th>Ad</th>
                <th>Vəziyyət</th>
                <th>Son giriş</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {boxes.map((b) => (
                <tr key={b.id} className={b.active ? '' : 'is-off'}>
                  <td>
                    <code>{b.email}</code>
                    {b.isTenantAdmin ? <span className="tag">admin</span> : null}
                    {b.email === user.mailboxEmail ? <span className="tag is-you">siz</span> : null}
                  </td>
                  <td>{b.displayName ?? '—'}</td>
                  <td>
                    {b.active ? (
                      <span className="state is-ok">aktiv</span>
                    ) : (
                      <span className="state is-off">deaktiv</span>
                    )}
                  </td>
                  <td className="dim">
                    {b.lastLoginAt ? fullDate(b.lastLoginAt) : 'heç vaxt'}
                  </td>
                  <td className="row-actions">
                    <button
                      className="btn-text"
                      disabled={busy}
                      onClick={() => {
                        setResetFor(resetFor === b.id ? null : b.id);
                        setResetPass('');
                      }}
                    >
                      Şifrə
                    </button>
                    <button
                      className="btn-text"
                      disabled={busy}
                      onClick={() => {
                        // ⚠️ DEAKTİVLƏŞDİRMƏ GƏLƏN POÇTU DA KƏSİR.
                        // Postfix `mailboxes.active` sütununa baxır: qutu
                        // deaktivdirsə məktub QƏBUL EDİLMİR və göndərənə geri
                        // qayıdır. Əvvəl heç bir xəbərdarlıq yox idi — «işçi
                        // məzuniyyətdədir, müvəqqəti bağlayaq» deyən admin
                        // əslində ona gələn bütün məktubları itirirdi.
                        // Aktivləşdirmə zərərsizdir, təsdiq istəmirik.
                        if (!b.active) {
                          void run(`${b.email} aktivləşdirilir`, () =>
                            api.updateMailbox(b.id, { active: true }),
                          );
                          return;
                        }
                        void dlg
                          .ask({
                            title: `${b.email} deaktiv edilsin?`,
                            body:
                              'Bu ünvana gələn məktublar QƏBUL EDİLMƏYƏCƏK — göndərənə ' +
                              'geri qayıdacaq. Mövcud məktublar silinmir və istənilən vaxt ' +
                              'yenidən aktivləşdirə bilərsiniz.',
                            confirmLabel: 'Deaktiv et',
                            danger: true,
                          })
                          .then((ok) => {
                            if (ok === null) return;
                            return run(`${b.email} deaktiv edilir`, () =>
                              api.updateMailbox(b.id, { active: false }),
                            );
                          });
                      }}
                    >
                      {b.active ? 'Deaktiv et' : 'Aktivləşdir'}
                    </button>
                    <button
                      className="btn-text btn-danger"
                      disabled={busy}
                      onClick={() => {
                        // ⚠️ Təsdiq MƏCBURİDİR: qutu silinəndə poçtu da itir.
                        void dlg
                          .ask({
                            title: `${b.email} silinsin?`,
                            body:
                              'Qutu və İÇİNDƏKİ BÜTÜN MƏKTUBLAR həmişəlik silinir. ' +
                              'Geri qaytarmaq mümkün olmayacaq.',
                            confirmLabel: 'Qutunu sil',
                            danger: true,
                          })
                          .then((ok) => {
                            if (ok === null) return;
                            return run(`${b.email} silinir`, () => api.deleteMailbox(b.id));
                          });
                      }}
                    >
                      Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Şifrə sıfırlama paneli */}
        {resetFor ? (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              const box = boxes.find((x) => x.id === resetFor);
              void run(`${box?.email} şifrəsi dəyişilir`, async () => {
                await api.updateMailbox(resetFor, { password: resetPass });
                setResetFor(null);
                setResetPass('');
              });
            }}
          >
            <span className="compose-label">Yeni şifrə</span>
            <input
              autoFocus
              type="text"
              minLength={10}
              placeholder="ən azı 10 simvol"
              value={resetPass}
              onChange={(e) => setResetPass(e.target.value)}
            />
            <button type="submit" className="btn-primary" disabled={busy || resetPass.length < 10}>
              Təyin et
            </button>
            <button type="button" className="btn-text" onClick={() => setResetFor(null)}>
              Ləğv
            </button>
          </form>
        ) : null}
      </section>

      {/* ── Yeni qutu ── */}
      {/*
        ⚠️ LƏQƏBLƏR «YENİ QUTU»DAN ƏVVƏL GƏLİR — QƏSDƏN.
        Yeni ünvana ehtiyacı olan adam ilk növbədə qutu yaratmağa gedir,
        çünki başqa yol görmür. Halbuki hallarının çoxunda ona ləqəb
        kifayət edir və ləqəb PULSUZDUR. Ucuz variantı bahalı variantdan
        sonra göstərmək müştəriyə lazım olmayan xərc yükləmək deməkdir.
      */}
      <Aliases domains={domains} mailboxes={boxes} />

      <section className="admin-card">
        <h2>Yeni poçt qutusu</h2>
        {activeDomains.length === 0 ? (
          <p className="onboard-note">
            Qutu yaratmaq üçün əvvəlcə aktiv domen lazımdır — DNS yoxlamasını tamamlayın.
          </p>
        ) : (
          <form className="inline-form" onSubmit={createBox}>
            <input
              required
              placeholder="ad"
              value={newLocal}
              onChange={(e) => setNewLocal(e.target.value)}
            />
            <span className="addon">@</span>
            <select value={newDomainId} onChange={(e) => setNewDomainId(e.target.value)}>
              {activeDomains.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.domain}
                </option>
              ))}
            </select>
            <input
              required
              type="text"
              minLength={10}
              placeholder="şifrə (10+ simvol)"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
            />
            <button
              type="submit"
              className="btn-primary"
              disabled={busy || !newLocal.trim() || newPass.length < 10}
            >
              Yarat
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
